import React from 'react';
import { Box, Typography } from '@mui/material';
import { ExecutiveSummaryResponse, WorkloadSimulatorResponse } from '../../services/benchmarkService';
import { NotComputed, PanelSkeleton, TierPill } from './BenchmarkPrimitives';
import { RETENTION_FLOOR, missedCount, suggestPolicy } from './benchmarkModel';
import { count, percent, tierSetLabel } from './BenchmarkVocabulary';
import { dsVar, ink } from '../../theme/tokens';

interface BenchmarkVerdictProps {
    summary: ExecutiveSummaryResponse | null;
    simulator: WorkloadSimulatorResponse | null;
    /** Too few cases to recommend a change; the figures are stated as indicative. */
    indicative?: boolean;
    loading?: boolean;
}

/**
 * The recommendation, as a before-and-after.
 *
 * An AML operations lead opens this page with one question: should we change how
 * alerts are triaged, and what would it cost. That has to be answerable from one
 * paragraph — stated as what happens today versus what would happen instead,
 * with the price of the change named rather than implied.
 *
 * The earlier version led with "Suggested cut-off" and a sentence that assumed
 * the reader already knew what a cut-off was, what the caseload was today, and
 * that "94.3% of the cases investigators confirmed" implied giving up the rest.
 */
const BenchmarkVerdict: React.FC<BenchmarkVerdictProps> = ({ summary, simulator, indicative = false, loading }) => {
    if (loading) return <PanelSkeleton height={210} />;
    if (!summary || !simulator) return null;

    const suggestion = suggestPolicy(simulator.reviewPolicies);

    if (!suggestion) {
        return (
            <Box className="rounded-2xl bg-surface/80 border border-line p-6">
                <NotComputed detail="This backtest did not record enough tier detail to recommend a review level." />
            </Box>
        );
    }

    const { policy, meetsFloor } = suggestion;
    const accent = indicative ? '#94A3B8' : meetsFloor ? '#10B981' : '#FBBF24';

    const confirmedMissed = missedCount(summary.totals.truePositives, policy.truePositiveRecall);
    const sarsMissed = missedCount(summary.totals.sarsFiled, policy.sarRecall);

    return (
        <Box
            className="rounded-2xl p-6"
            sx={{
                backgroundColor: dsVar('surface', 0.9),
                border: `1px solid ${accent}59`,
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)'
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em',
                    textTransform: 'uppercase', color: dsVar('fg-muted'), mb: 1.5
                }}
            >
                {indicative ? 'What this sample suggests — indicative only' : 'Recommended change'}
            </Typography>

            {/*
              Today first. Without the "all N alerts" anchor, a percentage
              reduction has nothing to be a reduction FROM.
            */}
            <Typography sx={{ fontSize: '1.0625rem', color: dsVar('fg'), lineHeight: 1.7 }}>
                Today your investigators work all{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>{count(simulator.totalAlertCount)} alerts</Box>
                {' '}in this caseload. If they worked only{' '}
                <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, verticalAlign: 'middle', mx: 0.25 }}>
                    {policy.activeTiers.map((tier) => <TierPill key={tier} code={tier} showCode={false} />)}
                </Box>
                {' '}priority alerts, they would work{' '}
                <Box component="span" sx={{ color: accent, fontWeight: 800 }}>
                    {count(policy.alertsReviewed)} — {percent(policy.workloadSavedFraction, 0)} fewer
                </Box>
                {' '}— and still reach{' '}
                <Box component="span" sx={{ color: accent, fontWeight: 800 }}>
                    {percent(policy.truePositiveRecall, 0)}
                </Box>
                {' '}of the cases investigators confirmed and{' '}
                <Box component="span" sx={{ color: accent, fontWeight: 800 }}>
                    {percent(policy.sarRecall, 0)}
                </Box>
                {' '}of the SARs filed.
            </Typography>

            {/*
              The price of the change, as counts rather than the inverse of a
              percentage. "94% retained" does not read as "26 confirmed cases
              closed unseen", and the second is what a compliance officer is
              accountable for.
            */}
            {(confirmedMissed !== null || sarsMissed !== null) && (
                <Box
                    sx={{
                        mt: 2.5, p: 2, borderRadius: '8px',
                        backgroundColor: dsVar('page', 0.5),
                        borderLeft: '3px solid #F97316'
                    }}
                >
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.6 }}>
                        <Box component="span" sx={{ fontWeight: 700, color: dsVar('fg') }}>What it costs: </Box>
                        on this caseload,{' '}
                        {confirmedMissed !== null && (
                            <>
                                <Box component="span" sx={{ fontWeight: 700, color: ink('#FB923C') }}>
                                    {count(confirmedMissed)} of {count(summary.totals.truePositives)}
                                </Box>
                                {' '}confirmed {confirmedMissed === 1 ? 'case' : 'cases'}
                            </>
                        )}
                        {confirmedMissed !== null && sarsMissed !== null && ' and '}
                        {sarsMissed !== null && (
                            <>
                                <Box component="span" sx={{ fontWeight: 700, color: ink('#FB923C') }}>
                                    {count(sarsMissed)} of {count(summary.totals.sarsFiled)}
                                </Box>
                                {' '}{sarsMissed === 1 ? 'SAR' : 'SARs'}
                            </>
                        )}
                        {' '}would have been closed without an investigator opening them.
                    </Typography>
                </Box>
            )}

            {/* The rule behind the suggestion, as a footnote rather than mid-sentence. */}
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 2, lineHeight: 1.6 }}>
                {indicative && `This sample has only ${count(simulator.totalAlertCount)} cases, too few to recommend a review level. `}
                {meetsFloor
                    ? `Of the review levels this backtest supports, this is the largest reduction that still keeps at least ${percent(RETENTION_FLOOR, 0)} of confirmed cases.`
                    : `No review level in this backtest keeps ${percent(RETENTION_FLOOR, 0)} of confirmed cases — this is the most protective one available.`}
                {' '}Adjust the slider below to weigh any other level.
            </Typography>

            {summary.rescued.total > 0 && (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), mt: 2.5, pt: 2.5, borderTop: `1px solid ${dsVar('line')}`, lineHeight: 1.6 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: dsVar('fg') }}>Separately: </Box>
                    the agent raised{' '}
                    <Box component="span" sx={{ color: dsVar('accent-fg'), fontWeight: 700 }}>{count(summary.rescued.total)}</Box>
                    {' '}alerts out of {tierSetLabel(summary.rescued.fromTiers)} priority that investigators went on to confirm
                    {summary.rescued.sarFilings > 0 && `, ${count(summary.rescued.sarFilings)} of which led to a SAR`}.
                    Your current rules had ranked these low enough to be worked late or not at all.
                </Typography>
            )}
        </Box>
    );
};

export default BenchmarkVerdict;
